import type { Candle, ContractWatch, Market, OddsQuote, SportsbookMarketType } from '../domain/types'
import { contractEv, crossedTarget, movementAlert, noAskCents, requiredEntryPriceCents, spreadCents } from './math/predictionMarket'
import { americanToDecimal, decimalToAmerican } from './math/odds'

export function parseCsv(text: string): string[][] {
  const clean = text.replace(/^\uFEFF/, '')
  const rows: string[][] = []
  let field = ''
  let row: string[] = []
  let inQuotes = false

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]
    if (inQuotes) {
      if (ch === '"') {
        if (clean[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
      continue
    }
    if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      row.push(field)
      field = ''
    } else if (ch === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else if (ch === '\r') {
      // handled by the \n branch
    } else {
      field += ch
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows.filter((r) => !(r.length === 1 && r[0].trim() === ''))
}

export interface ParsedRecords {
  headers: string[]
  records: Record<string, string>[]
}

export function parseRecords(text: string): ParsedRecords {
  const rows = parseCsv(text)
  if (rows.length === 0) return { headers: [], records: [] }
  const headers = rows[0].map((h) => h.trim().toLowerCase())
  const records: Record<string, string>[] = []
  for (let i = 1; i < rows.length; i++) {
    const record: Record<string, string> = {}
    for (let j = 0; j < headers.length; j++) record[headers[j]] = (rows[i][j] ?? '').trim()
    records.push(record)
  }
  return { headers, records }
}

function num(value: string | undefined, fallback = 0): number {
  if (value === undefined) return fallback
  const n = Number(value.replace(/[$,%]/g, ''))
  return Number.isFinite(n) ? n : fallback
}

function pick(record: Record<string, string>, keys: string[]): string | undefined {
  for (const k of keys) {
    if (record[k] !== undefined && record[k] !== '') return record[k]
  }
  return undefined
}

export interface CsvParseResult<T> {
  rows: T[]
  errors: string[]
}

export function parseOhlcvCsv(text: string): CsvParseResult<Candle> {
  const { records } = parseRecords(text)
  const rows: Candle[] = []
  const errors: string[] = []
  records.forEach((r, idx) => {
    const time = pick(r, ['time', 'date', 'timestamp', 'day'])
    const close = pick(r, ['close', 'adj close', 'adjclose', 'last'])
    if (!time || close === undefined) {
      errors.push(`Row ${idx + 2}: missing date or close.`)
      return
    }
    const closeNum = num(close)
    const open = num(pick(r, ['open']), closeNum)
    const high = num(pick(r, ['high']), Math.max(open, closeNum))
    const low = num(pick(r, ['low']), Math.min(open, closeNum))
    rows.push({
      time,
      open,
      high: Math.max(high, open, closeNum),
      low: Math.min(low, open, closeNum),
      close: closeNum,
      volume: num(pick(r, ['volume', 'vol']), 0),
    })
  })
  rows.sort((a, b) => a.time.localeCompare(b.time))
  return { rows, errors }
}

export interface OddsCsvRow extends Omit<OddsQuote, 'id'> {
  id?: string
}

export function parseOddsCsv(text: string): CsvParseResult<OddsCsvRow> {
  const { records } = parseRecords(text)
  const rows: OddsCsvRow[] = []
  const errors: string[] = []
  records.forEach((r, idx) => {
    const event = pick(r, ['event', 'game', 'match'])
    const selection = pick(r, ['selection', 'team', 'pick', 'side'])
    if (!event || !selection) {
      errors.push(`Row ${idx + 2}: missing event or selection.`)
      return
    }
    const americanRaw = pick(r, ['american', 'american odds', 'moneyline', 'ml'])
    const decimalRaw = pick(r, ['decimal', 'decimal odds', 'price'])
    let decimalOdds: number
    let americanOdds: number
    if (decimalRaw !== undefined) {
      decimalOdds = num(decimalRaw, 2)
      americanOdds = decimalToAmerican(decimalOdds)
    } else if (americanRaw !== undefined) {
      americanOdds = num(americanRaw, 100)
      decimalOdds = americanToDecimal(americanOdds)
    } else {
      errors.push(`Row ${idx + 2}: no decimal or american odds.`)
      return
    }
    const marketType = (pick(r, ['markettype', 'market', 'type']) ?? 'moneyline') as SportsbookMarketType
    rows.push({
      event,
      sport: pick(r, ['sport', 'league', 'category']) ?? 'unknown',
      marketType,
      selection,
      format: 'decimal',
      decimalOdds,
      americanOdds,
      line: pick(r, ['line', 'spread', 'total']) ? num(pick(r, ['line', 'spread', 'total'])) : undefined,
      book: pick(r, ['book', 'sportsbook']),
      timestamp: pick(r, ['timestamp', 'date', 'time']) ?? new Date().toISOString(),
      closingDecimal: pick(r, ['closing', 'close', 'closing decimal'])
        ? num(pick(r, ['closing', 'close', 'closing decimal']))
        : undefined,
      modelProb: pick(r, ['modelprob', 'model prob', 'fair']) ? num(pick(r, ['modelprob', 'model prob', 'fair'])) : undefined,
      legal: true,
    })
  })
  return { rows, errors }
}

export function toCsv(records: ReadonlyArray<Record<string, unknown>>, headers?: string[]): string {
  if (records.length === 0) return headers ? headers.join(',') + '\n' : ''
  const cols = headers ?? Object.keys(records[0])
  const escape = (value: unknown): string => {
    const s = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [cols.join(',')]
  for (const record of records) lines.push(cols.map((c) => escape(record[c])).join(','))
  return lines.join('\n') + '\n'
}

export const MARKET_IDEA_CSV_HEADERS = [
  'market_id',
  'market_title',
  'category',
  'source',
  'correlation_group',
  'mutually_exclusive',
  'resolution_date',
  'contract_id',
  'contract_title',
  'settlement',
  'settled_at',
  'yes_bid_cents',
  'yes_ask_cents',
  'last_cents',
  'spread_cents',
  'volume',
  'liquidity_score',
  'model_prob',
  'yes_ev_cents',
  'yes_ev_per_dollar',
  'no_ev_cents',
  'no_ev_per_dollar',
  'worth_it_yes_cents',
  'watch_side',
  'target_entry_cents',
  'target_crossed',
  'movement_cents',
  'movement_alert',
  'resolution_source',
  'resolution_criteria',
]

export function marketIdeasToCsvRows(markets: readonly Market[], watches: readonly ContractWatch[] = []): Array<Record<string, unknown>> {
  const watchByContract = new Map(watches.map((w) => [w.contractId, w]))
  return markets.flatMap((market) =>
    market.contracts.map((contract) => {
      const priceHistory = (contract as { priceHistory?: typeof contract.priceHistory }).priceHistory ?? [
        { time: '', yesPriceCents: contract.lastCents },
      ]
      const modelProb = contract.modelProb
      const yesEv =
        modelProb === undefined
          ? null
          : contractEv({ side: 'yes', modelProb, yesBidCents: contract.bidCents, yesAskCents: contract.askCents })
      const noEv =
        modelProb === undefined
          ? null
          : contractEv({ side: 'no', modelProb, yesBidCents: contract.bidCents, yesAskCents: contract.askCents })
      const watch = watchByContract.get(contract.id)
      const alert = movementAlert(priceHistory)
      return {
        market_id: market.id,
        market_title: market.title,
        category: market.category,
        source: market.source,
        correlation_group: market.correlationGroup ?? '',
        mutually_exclusive: market.mutuallyExclusive ?? false,
        resolution_date: market.resolutionDate ?? '',
        contract_id: contract.id,
        contract_title: contract.title,
        settlement: contract.settlement ?? '',
        settled_at: contract.settledAt ?? '',
        yes_bid_cents: contract.bidCents,
        yes_ask_cents: contract.askCents,
        last_cents: contract.lastCents,
        spread_cents: spreadCents(contract.bidCents, contract.askCents),
        volume: contract.volume,
        liquidity_score: contract.liquidityScore,
        model_prob: modelProb ?? '',
        yes_ev_cents: yesEv?.evCents ?? '',
        yes_ev_per_dollar: yesEv?.evPerDollar ?? '',
        no_ev_cents: noEv?.evCents ?? '',
        no_ev_per_dollar: noEv?.evPerDollar ?? '',
        worth_it_yes_cents: modelProb === undefined ? '' : requiredEntryPriceCents(modelProb, 'yes', 0.02),
        watch_side: watch?.side ?? '',
        target_entry_cents: watch?.targetPriceCents ?? '',
        target_crossed: watch
          ? crossedTarget(watch.side === 'yes' ? contract.askCents : noAskCents(contract.bidCents), watch.targetPriceCents)
          : '',
        movement_cents: alert.changeCents,
        movement_alert: alert.significant ? alert.message : '',
        resolution_source: contract.resolutionSource ?? '',
        resolution_criteria: contract.resolutionCriteria ?? '',
      }
    }),
  )
}
